import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Reveal, Eyebrow, EASE } from "@/components/primitives";

const SERVICES = [
  {
    title: "Design Solutions",
    points: ["Civil & Interior & MEPF Design", "Project Research", "Optimized Space Planning", "Functional & Sustainable Designs"],
  },
  {
    title: "Build Solutions",
    points: ["Turnkey Interior Solutions", "Interior Fit-out Solutions", "General Contracting"],
  },
  {
    title: "Design & Build",
    points: ["Architecture", "Interior Design", "MEP", "Project Management", "Execution"],
  },
  {
    title: "Engineering & Operations",
    points: ["End-to-End Service Execution", "Quality Assurance & Control", "Integration of Advanced Technologies", "Customization of Engineering Solutions"],
  },
];

export default function Services({ cta = false, heading = true }) {
  const [open, setOpen] = useState(0);
  const navigate = useNavigate();

  return (
    <section id="services" className="bg-paper px-6 pb-24 pt-4 lg:px-12 lg:pb-36" data-testid="services-section">
      <div className="mx-auto max-w-[1440px]">
        {heading && (
          <>
            <Reveal>
              <Eyebrow index="02">What we do</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl text-ink sm:text-5xl">Our Services</h2>
            </Reveal>
          </>
        )}

        <div className={`border-t border-ink/15 ${heading ? "mt-14" : "mt-0"}`}>
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.05}>
              <div className="border-b border-ink/15 transition-colors duration-500 hover:bg-navy-deep/5">
                <button
                  onClick={() => setOpen(open === i ? -1 : i)}
                  data-testid={`service-accordion-${i + 1}`}
                  aria-expanded={open === i}
                  className="group flex w-full items-center gap-6 py-8 text-left lg:gap-10 lg:px-4"
                >
                  <span className="font-sans text-sm font-bold text-navy">0{i + 1}</span>
                  <h3 className="flex-1 font-serif text-3xl text-ink transition-colors duration-300 group-hover:text-navy lg:text-4xl">
                    {s.title}
                  </h3>
                  <motion.span
                    animate={{ rotate: open === i ? 45 : 0 }}
                    transition={{ duration: 0.35, ease: EASE }}
                    className="text-3xl font-light text-ink/50"
                  >
                    +
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {open === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.45, ease: EASE }}
                      className="overflow-hidden"
                      data-testid={`service-accordion-content-${i + 1}`}
                    >
                      <div className="flex flex-wrap gap-2.5 pb-9 pl-12 lg:pl-14 lg:pr-4">
                        {s.points.map((p) => (
                          <span
                            key={p}
                            className="rounded-full border border-navy/25 bg-navy-deep/5 px-4 py-2 text-xs text-navy"
                          >
                            {p}
                          </span>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          ))}
        </div>

        {cta && (
          <Reveal delay={0.1}>
            <div className="mt-14 flex justify-center">
              <button
                onClick={() => navigate("/services")}
                data-testid="services-page-button"
                className="group flex items-center gap-3 rounded-full bg-maroon px-10 py-4 text-[12px] font-medium uppercase tracking-[0.2em] text-paper transition-transform duration-300 hover:scale-[1.04]"
              >
                Our Services
                <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
              </button>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
